import React, { useState, useEffect, useCallback } from "react";
import styles from '../Styles/LoginStyles'; // Import styles
import Header from '../Components/Header'; // Import Header
import { auth, sendSignInLinkToEmail, isSignInWithEmailLink, signInWithEmailLink, googleProvider, signInWithPopup } from "../utils/firebase";
import { isEmailWhitelisted, db } from "../utils/firestore"; // Whitelist validation function
import { useNavigate } from "react-router-dom"; // Import useNavigate for navigation
import { setDoc, doc, serverTimestamp } from "firebase/firestore"; // Import setDoc and doc
import { fetchSavedAnalyses } from "../utils/firestore"; // Import fetchSavedAnalyses
import googleButton from "../assets/Google_signin_button.png"; // Google sign-in button

// List of quotes
const quotes = [
  "“Failing to plan is planning to fail.” – Benjamin Franklin",
  "“A goal without a plan is just a wish.” – Antoine de Saint-Exupéry",
  "“Plans are nothing; planning is everything.” – Dwight D. Eisenhower",
  "“An hour of planning can save you 10 hours of doing.” – Dale Carnegie",
  "“Good fortune is what happens when opportunity meets with planning.” – Thomas Edison",
  "“Give me six hours to chop down a tree and I will spend the first four sharpening the axe.” – Abraham Lincoln",
  "“A man who does not plan long ahead will find trouble at his door.” – Confucius",
];

const Login = () => {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [appLoading, setAppLoading] = useState(false); // App loading state
  const [randomQuote, setRandomQuote] = useState(""); // State for random quote
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false); // State for Privacy Policy modal
  const navigate = useNavigate();
  const isWhitelistEnabled = false; // Set to true to enable whitelist check

  useEffect(() => {
    setRandomQuote(quotes[Math.floor(Math.random() * quotes.length)]);
    const interval = setInterval(() => {
      setRandomQuote((prevQuote) => {
        let newQuote;
        do {
          newQuote = quotes[Math.floor(Math.random() * quotes.length)];
        } while (newQuote === prevQuote); // Ensure a new quote is picked
        return newQuote;
      });
    }, 7000); // Change quote every 5 seconds (adjust as needed)
  
    return () => clearInterval(interval); // Cleanup interval on component unmount
  }, []); // Empty dependency array to ensure it only runs once
  

  const handleGoogleSignIn = async () => {
    try {
      setAppLoading(true);
  
      // 🔥 Sign in with Google
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      console.log("✅ Signed in as:", user);
  
      // 🔥 Log Google sign-in to Firestore (handle errors gracefully)
      try {
        await setDoc(doc(db, "signins", user.uid), {
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          timestamp: serverTimestamp(),
          method: "google",
        });
        console.log("✅ Sign-in recorded in Firestore.");
      } catch (firestoreError) {
        console.warn("⚠️ Failed to log sign-in to Firestore:", firestoreError);
      }
  
      // 🔥 Fetch saved analyses before navigating
      const savedAnalyses = await fetchSavedAnalyses(); // Ensure analyses are loaded before navigating
      console.log("✅ Fetched saved analyses:", savedAnalyses);
  
      // ✅ Navigate to PromptFlow AFTER fetching saved analyses
      setMessage("Sign-in successful! Redirecting...");
      navigate("/PromptFlow"); // No need for setTimeout
  
    } catch (err) {
      console.error("🔥 Google sign-in error:", err);
      setError(err.message);
    } finally {
      setAppLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault(); // Prevent form submission from refreshing the page
    setLoading(true); // Indicate that the email submission is in progress
    setError(""); // Clear any previous error messages

    try {
        // Check if the whitelist is enabled
        if (isWhitelistEnabled) {
            // Check if the entered email is in the whitelist
            const whitelisted = await isEmailWhitelisted(email);
            if (!whitelisted) {
                setError("This email is not authorized to access the app.");
                return; // Exit the function early if the email is not whitelisted
            }
        }

      // Define the settings for the email sign-in link
      const actionCodeSettings = {
        url: `${window.location.origin}/login`, // Redirect to the login page
        handleCodeInApp: true, // Ensure the sign-in link is handled in the app
      };

      // Send the sign-in link to the entered email
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);

      // Store the email in localStorage for later verification
      window.localStorage.setItem("emailForSignIn", email);

      // Set a success message to inform the user
      setMessage("Sign-in link sent! Check your email.");
    } catch (err) {
      // Handle errors that occur during the process
      setError(err.message);
    } finally {
      // Ensure the loading state is reset regardless of success or failure
      setLoading(false);
    }
  };

  const handleSignInWithLink = useCallback(async () => {
    try {
      if (isSignInWithEmailLink(auth, window.location.href)) {
        let email = window.localStorage.getItem("emailForSignIn");
  
        if (!email) {
          email = prompt("Please provide your email for confirmation:");
          if (!email) {
            throw new Error("Email confirmation is required to complete the sign-in.");
          }
        }
  
        setAppLoading(true);
  
        // Attempt to sign in with the email link
        const result = await signInWithEmailLink(auth, email, window.location.href);
        console.log("Signed in as:", result.user);
  
        // Log sign-in to Firestore signins collection
        await setDoc(doc(db, "signins", result.user.uid), { 
          email, 
          timestamp: serverTimestamp(),
        });
  
        // Clear email from local storage after successful sign-in
        window.localStorage.removeItem("emailForSignIn");
  
        // 🔥 Ensure Firebase Auth is fully updated before proceeding
        await new Promise((resolve) => setTimeout(resolve, 1000));
  
        // 🔥 Fetch saved analyses before navigating
        const savedAnalyses = await fetchSavedAnalyses(); // ✅ Use returned data
        console.log("✅ Fetched saved analyses:", savedAnalyses);
  
        // Navigate to PromptFlow AFTER fetching saved analyses
        setMessage("Sign-in successful! Redirecting to the app...");
        setTimeout(() => {
          navigate("/PromptFlow"); 
        }, 500);
      }
    } catch (err) {
      console.error("Error during sign-in with email link:", err);
      setError(err.message);
    } finally {
      setAppLoading(false);
    }  
  }, [navigate]);

  useEffect(() => {
    if (isSignInWithEmailLink(auth, window.location.href)) {
      handleSignInWithLink();
    }
  }, [handleSignInWithLink]); // Include handleSignInWithLink in the dependency array

  return (
    <div>
      <Header />
      <div style={styles.wrapper}>
        {/* Welcome Blurb */}
        <div style={styles.blurbContainer}>
          <h2 style={{ fontSize: "1.2em", fontWeight: "600", color: "#333", marginBottom: "8px" }}>
          Exploring a startup idea? 
          </h2>
          <p style={{ fontSize: "1em", color: "#555", lineHeight: "1.5" }}>
            AIVAN helps you uncover promising market opportunities and pinpoint your ideal launch segment.  
            Get tailored insights and a recommended validation strategy — so you can forge a compelling value proposition and build with confidence.
          </p>
        </div>
        {/* Login Box */}
        <div style={styles.formContainer}>
          {appLoading ? (
            // Show loading view when the app is loading
            <p style={{ ...styles.message, color: "blue" }}>Loading application... Please wait.</p>
          ) : (
            <>
              <h2 style={styles.startJourneyHeading}>
                Start Your Journey Now
                <span style={{ 
                  display: "block", 
                  width: "60px", 
                  height: "4px", 
                  backgroundColor: "#3498db", 
                  margin: "8px auto 0",
                  borderRadius: "2px"
                }}></span>
              </h2>
              <br />
              <div>
                <button onClick={handleGoogleSignIn} style={{ border: "none", background: "none" }}>
                  <img src={googleButton} alt="Sign in with Google" style={{ width: "200px" }} />
                </button>
              </div>
              <br />
              <hr />
              <h3>or get a sign-in link by email</h3>

              {/* Show success message and hide form if sign-in link is sent */}
              {message ? (
                <p style={{ ...styles.message, color: "green" }}>{message}</p>
              ) : (
                <>
                  <form onSubmit={handleEmailSubmit}>
                    <input
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError(""); // Clear error when user starts typing
                      }}
                      required
                      disabled={loading}
                      style={styles.input}
                    />
                    <button
                      type="submit"
                      disabled={loading}
                      style={styles.button}
                    >
                      {loading ? "Sending..." : "Send Sign-In Link"}
                    </button>
                  </form>
                  {/* Show error message for non-whitelist emails */}
                  {error && <p style={{ ...styles.message, color: "red" }}>{error}</p>}
                </>
              )}
            </>
          )}
        </div>
        {/* ✅ "How it Works" Image (Added Below Login Form) */}
        <div style={styles.howItWorksContainer}>
          <h3>How It Works</h3>
          <img
            src="/how-it-works.png"
            alt="How it Works"
            style={styles.howItWorksImage}
          />
        </div>
        {/* Random Quote Display */}
        <div style={styles.quoteContainer}>
          <p style={styles.quote}>{randomQuote}</p>
        </div>
        {/* Privacy Policy Link */}
        <div style={{ textAlign: "center", marginTop: "20px" }}>
        <button onClick={() => setShowPrivacyPolicy(true)} style={styles.privacyLink}>
          Privacy Policy
        </button>
        </div>
      </div>

      {/* Privacy Policy Modal */}
      {showPrivacyPolicy && (
        <div style={{
          position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
          backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center"
        }}>
          <div style={{ backgroundColor: "#fff", padding: "20px", borderRadius: "8px", width: "80%", maxWidth: "600px" }}>
            <h2>Privacy Policy</h2>
            <p><strong>1. Introduction</strong></p>
            <p>Welcome to AIVAN, your AI-powered venture copilot. Your privacy is important to us. This Privacy Policy explains how we handle your data while you use our app.</p>
            <p><strong>2. Data Collection & Usage</strong></p>
            <p>AIVAN does not collect, store, or share any personal information or startup ideas entered into the app. All inputs provided by users are processed in real time to generate insights but are not retained, logged, or used for any other purpose.</p>
            <p><strong>3. AI Processing & Security</strong></p>
            <p>AIVAN leverages OpenAI’s language model (LLM) to provide guidance. OpenAI follows strict security and privacy protocols, ensuring that data processed by the model is not used to train future versions or shared with third parties.</p>
            <p><strong>4. No Idea Retention or Use</strong></p>
            <p>We understand that your startup ideas are valuable. AIVAN does not save, analyze, or repurpose any user input beyond the immediate session. Once you close the app, your data is gone.</p>
            <p><strong>5. Future Updates & Monetization</strong></p>
            <p>AIVAN is currently free to use. As we explore monetization options, any changes to data policies will be communicated transparently.</p>
            <p><strong>Last Updated:</strong>16-Feb 2025</p>
            <button onClick={() => setShowPrivacyPolicy(false)} style={{ display: "block", margin: "10px auto", padding: "8px 16px", backgroundColor: "#3498db", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer" }}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;